/**
 * Rebuilding a scene from a list of entry sources plus a component list, shared by the ChatGPT widget (tool output
 * and widget state) and the Open in Nanome handoff page (/open?s=<token>, session from the MCP Worker).
 *
 * Entry sources use the ?src= syntax: `rcsb:1CRN` or `url:https://…`. Entries load in order, so the serials the
 * components were written against (1..N in the originating viewer) are remapped to the serials this store assigned.
 */
import type { Component, FilterModel, LiteStore } from '@nanome-lite/state';
import type { ColoringModel, VisualizationModel } from '@nanome-lite/state';

export interface EntryRef { serial: number; name: string; source: string }

/** The MCP Worker origin (handoff sessions, proxied files). */
export const MCP_ORIGIN = (import.meta.env.VITE_MCP_ORIGIN ?? 'https://mcp.lite.nanome.ai').replace(/\/$/, '');

export async function loadSource(store: LiteStore, source: string): Promise<number> {
  if (source.startsWith('rcsb:')) return (await store.addEntryFromRcsb(source.slice(5))).serial;
  if (source.startsWith('url:')) return (await store.addEntryFromUrl(source.slice(4))).serial;
  throw new Error(`unsupported source ${source}`);
}

/** Load every entry; returns old serial -> new serial for the ones that loaded (failures are toasted by the store). */
export async function loadEntries(store: LiteStore, entries: EntryRef[]): Promise<Map<number, number>> {
  const map = new Map<number, number>();
  for (const e of entries) {
    try { map.set(e.serial, await loadSource(store, e.source)); }
    catch { /* toasted */ }
  }
  return map;
}

function remapFilters(filters: FilterModel[], map: Map<number, number>): FilterModel[] | null {
  const out: FilterModel[] = [];
  for (const f of filters) {
    const next: FilterModel = { ...f };
    if (f.kind === 'EntrySerial' && typeof f.entrySerial === 'number') {
      const s = map.get(f.entrySerial);
      if (s === undefined) return null;
      next.entrySerial = s;
    }
    if (f.containedFilters) {
      const inner = remapFilters(f.containedFilters, map);
      if (!inner) return null;
      next.containedFilters = inner;
    }
    out.push(next);
  }
  return out;
}

/** Remap EntrySerial filters; components that reference an entry that did not load are dropped. */
export function remapComponents(components: Component[], map: Map<number, number>): Component[] {
  const out: Component[] = [];
  for (const c of components) {
    const filters = remapFilters(c.filters ?? [], map);
    if (!filters) continue;
    out.push({ ...c, filters, visualizations: (c.visualizations ?? []) as VisualizationModel[], hidden: !!c.hidden, isDefault: !!c.isDefault, entrySerials: [] });
  }
  return out;
}

/** Outline check of untrusted component JSON (session fetch, widget state). */
export function asComponents(value: unknown): Component[] | null {
  if (!Array.isArray(value)) return null;
  const ok = value.every((c) => c && typeof c === 'object' && typeof (c as Component).serial === 'number' && Array.isArray((c as Component).filters) && Array.isArray((c as Component).visualizations)
    && (c as Component).visualizations.every((v) => v && typeof v.kind === 'string' && typeof (v.coloring as ColoringModel | undefined)?.kind === 'string'));
  return ok ? (value as Component[]) : null;
}

export function asEntries(value: unknown): EntryRef[] | null {
  if (!Array.isArray(value) || !value.length) return null;
  const out: EntryRef[] = [];
  value.forEach((e, i) => {
    if (e && typeof e === 'object' && typeof (e as EntryRef).source === 'string' && /^(rcsb:|url:https?:\/\/)/.test((e as EntryRef).source)) {
      out.push({ serial: typeof (e as EntryRef).serial === 'number' ? (e as EntryRef).serial : i + 1, name: String((e as EntryRef).name ?? ''), source: (e as EntryRef).source });
    }
  });
  return out.length ? out : null;
}

/** Load entries, then replace the default components with `components` (when given) in one non-undoable step. */
export async function restoreScene(store: LiteStore, entries: EntryRef[], components: Component[] | null): Promise<Map<number, number>> {
  const map = await loadEntries(store, entries);
  if (components && map.size) {
    const remapped = remapComponents(components, map);
    if (remapped.length || components.length === 0) store.replaceComponents(remapped, { undoable: false });
  }
  return map;
}

/** The entries of the current store as sources (for Open in Nanome from the widget). Entries loaded from a local file have no source. */
export function currentEntryRefs(store: LiteStore): EntryRef[] {
  const out: EntryRef[] = [];
  for (const e of store.entries) {
    const src = e.source.kind === 'rcsb' && e.source.ref ? `rcsb:${e.source.ref}` : e.source.kind === 'url' && e.source.ref ? `url:${e.source.ref}` : null;
    if (src) out.push({ serial: e.serial, name: e.name, source: src });
  }
  return out;
}
